import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Tooltip,
    CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';

import { listApiKeys, generateApiKey, revokeApiKey } from '../../services/externalApiKeyService';
import ConfirmModal from '../../components/modals/common/ConfirmModal';

function formatDate(value) {
    if (!value) return 'Never';
    return new Date(value).toLocaleString();
}

function ExternalApiKeysPanel() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [generateOpen, setGenerateOpen] = useState(false);
    const [applicationName, setApplicationName] = useState('');
    const [generating, setGenerating] = useState(false);
    const [generatedKey, setGeneratedKey] = useState(null);
    const [copied, setCopied] = useState(false);

    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revoking, setRevoking] = useState(false);

    const loadKeys = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listApiKeys();
            if (result.ok) {
                setKeys(result.data || []);
                setError('');
            } else {
                setError(result.data?.message || 'Failed to load API keys');
            }
        } catch (err) {
            setError('Failed to load API keys: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadKeys();
    }, [loadKeys]);

    const handleGenerateOpen = () => {
        setApplicationName('');
        setGeneratedKey(null);
        setCopied(false);
        setGenerateOpen(true);
    };

    const handleGenerateSubmit = async () => {
        if (!applicationName.trim()) return;
        setGenerating(true);
        try {
            const result = await generateApiKey(applicationName.trim());
            if (result.ok) {
                setGeneratedKey(result.data);
                await loadKeys();
            } else {
                setError(result.data?.message || 'Failed to generate API key');
            }
        } catch (err) {
            setError('Failed to generate API key: ' + err.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateClose = () => {
        setGenerateOpen(false);
        setGeneratedKey(null);
        setApplicationName('');
    };

    const handleCopy = () => {
        if (!generatedKey?.apiKey) return;
        // Bug #11829: navigator.clipboard only exists in a secure context (https
        // or localhost). On-prem installs and the X3 iframe run over plain http,
        // where it is undefined and the old direct call threw before setCopied
        // ran - the button silently did nothing at the only moment the key is
        // ever visible. copy-to-clipboard tries the async API first and falls
        // back to a selection + execCommand('copy'), which works over http.
        const ok = copy(generatedKey.apiKey);
        setCopied(ok);
    };

    const handleRevokeConfirm = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            const result = await revokeApiKey(revokeTarget._id);
            if (result.ok) {
                await loadKeys();
                setRevokeTarget(null);
            } else {
                setError(result.data?.message || 'Failed to revoke API key');
            }
        } catch (err) {
            setError('Failed to revoke API key: ' + err.message);
        } finally {
            setRevoking(false);
        }
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Application API Keys
                </Typography>
                <Button variant="outlined" onClick={handleGenerateOpen}>
                    Generate Key
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <CircularProgress size={24} />
            ) : keys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No API keys have been generated yet.
                </Typography>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '12px' }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>Application</TableCell>
                            <TableCell>Key Prefix</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Last Used</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.map((key) => (
                            <TableRow key={key._id}>
                                <TableCell>{key.applicationName}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{key.keyPrefix}…</TableCell>
                                <TableCell>
                                    <Chip
                                        label={key.isActive ? 'Active' : 'Revoked'}
                                        color={key.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                                <TableCell>{formatDate(key.createdAt)}</TableCell>
                                <TableCell align="right">
                                    {key.isActive && (
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => setRevokeTarget(key)}
                                        >
                                            Revoke
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </TableContainer>
            )}

            <Dialog open={generateOpen} onClose={generating ? undefined : handleGenerateClose} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Application API Key</DialogTitle>
                <DialogContent>
                    {!generatedKey ? (
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Application Name"
                            value={applicationName}
                            onChange={(e) => setApplicationName(e.target.value)}
                            sx={{ mt: 1 }}
                        />
                    ) : (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This key will only be shown once. Copy it now and store it securely — it cannot be retrieved again.
                            </Alert>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={generatedKey.apiKey}
                                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                                />
                                <Tooltip title={copied ? 'Copied' : 'Copy to clipboard'}>
                                    <IconButton onClick={handleCopy}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {!generatedKey ? (
                        <>
                            <Button onClick={handleGenerateClose} disabled={generating}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleGenerateSubmit}
                                disabled={generating || !applicationName.trim()}
                            >
                                {generating ? 'Generating…' : 'Generate'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="contained" onClick={handleGenerateClose}>Done</Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmModal
                isOpen={!!revokeTarget}
                title="Revoke API Key"
                message={revokeTarget ? `Revoke the API key for '${revokeTarget.applicationName}'? Calls using this key will be rejected immediately. This cannot be undone.` : ''}
                confirmLabel="Revoke"
                severity="error"
                loading={revoking}
                onConfirm={handleRevokeConfirm}
                onCancel={() => setRevokeTarget(null)}
            />
        </Box>
    );
}

export default ExternalApiKeysPanel;
